import { inject, Injectable, Signal, WritableSignal } from "@angular/core";

import { ColDef } from "ag-grid-enterprise";

import { TableComponent } from "@components/shared/tables/table/table.component";
import { CustomFieldCellRendererComponent } from "@shared/cell-renderers";
import { CustomFieldsResourceTypeEnum, RecordStateEnum } from "@shared/enums";
import { ICustomField } from "@shared/interfaces";

import { CustomFieldsService } from "./api";

interface TableWithColumnDefs {
  columnDefs: WritableSignal<ColDef[]>;
  tableComponent: Signal<TableComponent>;
}

@Injectable({ providedIn: "root" })
export class TableWithCustomFieldsService {
  private resourceType: CustomFieldsResourceTypeEnum;

  private readonly customFieldsService = inject(CustomFieldsService);

  private hasAddedCustomFields: boolean;

  init(resourceType: CustomFieldsResourceTypeEnum) {
    this.resourceType = resourceType;
    this.hasAddedCustomFields = false;
  }

  async addColumnDefs({ columnDefs, tableComponent }: TableWithColumnDefs) {
    if (this.hasAddedCustomFields) {
      return;
    }

    const customFields = await this.customFieldsService.getAll(
      this.resourceType,
      RecordStateEnum.ACTIVE,
    );

    this.addCustomFieldColumnDefs(columnDefs, customFields, tableComponent());
  }

  private addCustomFieldColumnDefs(
    existingColumnDefs: WritableSignal<ColDef[]>,
    customFields: ICustomField[],
    tableComponent: TableComponent,
  ) {
    const columnDefs = [...existingColumnDefs()];
    const customFieldColumns = this.customFieldColumnDefs(customFields);

    columnDefs.push(...customFieldColumns);
    existingColumnDefs.set(columnDefs);

    const customFieldColIds = customFieldColumns.map((col) => col.field);

    tableComponent.grid().api.refreshCells({
      columns: customFieldColIds,
      force: true,
    });

    this.hasAddedCustomFields = true;
  }

  private customFieldColumnDefs(customFields: ICustomField[]): ColDef[] {
    return customFields.map((customField) => ({
      headerName: customField.label,
      field: `customFields.${customField.id}`,
      valueGetter: (params) => {
        if (!params.data?.customFields) {
          return null;
        }
        const field = params.data.customFields.find(
          (cf) => cf.definition.label === customField.label,
        );

        return field?.value || null;
      },
      cellRenderer: CustomFieldCellRendererComponent,
      cellRendererParams: {
        customField,
      },
    }));
  }
}
